// src/modules/Legal/Privacy.js
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '../../contexts/LanguageContext';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px 24px 80px;

  @media (max-width: 768px) {
    padding: 100px 20px 60px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #111;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const LastUpdated = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111;
  margin-bottom: 16px;
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: #444;
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 16px 0;
  padding-left: 24px;

  li {
    font-size: 16px;
    line-height: 1.7;
    color: #444;
    margin-bottom: 8px;
  }
`;

const ContactInfo = styled.div`
  background: #f5f5f5;
  padding: 24px;
  border-radius: 8px;
  margin-top: 24px;

  p {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: #111;
    text-decoration: underline;
  }
`;

export const Privacy = () => {
  const { language } = useTranslation();

  // Conteúdo em português
  const contentPT = {
    title: 'Política de Privacidade',
    lastUpdated: 'Última atualização: Dezembro de 2024',
    sections: [
      {
        title: '1. Informações que Coletamos',
        content: `A Vega Robotics coleta informações que você nos fornece diretamente, como quando preenche formulários de contato, solicita orçamentos ou se inscreve em nossa newsletter. Essas informações podem incluir:`,
        list: [
          'Nome completo',
          'Endereço de e-mail corporativo',
          'Telefone',
          'Nome da empresa',
          'Cargo ou função',
          'Mensagens enviadas através de formulários'
        ]
      },
      {
        title: '2. Como Usamos suas Informações',
        content: 'Utilizamos as informações coletadas para:',
        list: [
          'Responder às suas solicitações e dúvidas',
          'Enviar propostas comerciais quando solicitado',
          'Enviar comunicações sobre nossos serviços (com seu consentimento)',
          'Melhorar nossos produtos e serviços',
          'Cumprir obrigações legais'
        ]
      },
      {
        title: '3. Compartilhamento de Informações',
        content: 'Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros para fins de marketing. Podemos compartilhar informações apenas nas seguintes situações:',
        list: [
          'Com prestadores de serviços que nos auxiliam em nossas operações',
          'Quando exigido por lei ou ordem judicial',
          'Para proteger nossos direitos legais'
        ]
      },
      {
        title: '4. Segurança dos Dados',
        content: 'Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações pessoais contra acesso não autorizado, alteração, divulgação ou destruição. No entanto, nenhum método de transmissão pela Internet é 100% seguro.'
      },
      {
        title: '5. Cookies e Tecnologias Similares',
        content: 'Nosso site pode utilizar cookies e tecnologias similares para melhorar sua experiência de navegação, analisar o tráfego do site e personalizar conteúdo. Você pode configurar seu navegador para recusar cookies, mas isso pode afetar a funcionalidade do site.'
      },
      {
        title: '6. Seus Direitos',
        content: 'De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem direito a:',
        list: [
          'Acessar seus dados pessoais',
          'Corrigir dados incompletos ou desatualizados',
          'Solicitar a eliminação de seus dados',
          'Revogar seu consentimento a qualquer momento',
          'Solicitar a portabilidade dos dados'
        ]
      },
      {
        title: '7. Retenção de Dados',
        content: 'Mantemos suas informações pessoais pelo tempo necessário para cumprir as finalidades descritas nesta política, a menos que um período de retenção mais longo seja exigido por lei.'
      },
      {
        title: '8. Alterações nesta Política',
        content: 'Podemos atualizar esta política periodicamente. Recomendamos que você revise esta página regularmente para se manter informado sobre nossas práticas de privacidade.'
      },
      {
        title: '9. Contato',
        content: 'Se você tiver dúvidas sobre esta política ou sobre como tratamos seus dados pessoais, entre em contato conosco:'
      }
    ],
    contact: {
      company: 'Vega Robotics',
      email: 'contato@vegarobotics.com.br',
      address: 'Av. Alan Turing, 345 - Sala 11, Campinas - SP, 13083-898'
    }
  };

  // Conteúdo em inglês
  const contentEN = {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: December 2024',
    sections: [
      {
        title: '1. Information We Collect',
        content: `Vega Robotics collects information that you provide directly to us, such as when you fill out contact forms, request quotes, or subscribe to our newsletter. This information may include:`,
        list: [
          'Full name',
          'Corporate email address',
          'Phone number',
          'Company name',
          'Job title or role',
          'Messages sent through forms'
        ]
      },
      {
        title: '2. How We Use Your Information',
        content: 'We use the collected information to:',
        list: [
          'Respond to your requests and inquiries',
          'Send commercial proposals when requested',
          'Send communications about our services (with your consent)',
          'Improve our products and services',
          'Comply with legal obligations'
        ]
      },
      {
        title: '3. Information Sharing',
        content: 'We do not sell, rent, or share your personal information with third parties for marketing purposes. We may share information only in the following situations:',
        list: [
          'With service providers who assist us in our operations',
          'When required by law or court order',
          'To protect our legal rights'
        ]
      },
      {
        title: '4. Data Security',
        content: 'We implement technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.'
      },
      {
        title: '5. Cookies and Similar Technologies',
        content: 'Our website may use cookies and similar technologies to improve your browsing experience, analyze site traffic, and personalize content. You can configure your browser to refuse cookies, but this may affect website functionality.'
      },
      {
        title: '6. Your Rights',
        content: 'In accordance with applicable data protection laws, you have the right to:',
        list: [
          'Access your personal data',
          'Correct incomplete or outdated data',
          'Request deletion of your data',
          'Revoke your consent at any time',
          'Request data portability'
        ]
      },
      {
        title: '7. Data Retention',
        content: 'We retain your personal information for as long as necessary to fulfill the purposes described in this policy, unless a longer retention period is required by law.'
      },
      {
        title: '8. Changes to this Policy',
        content: 'We may update this policy periodically. We recommend that you review this page regularly to stay informed about our privacy practices.'
      },
      {
        title: '9. Contact',
        content: 'If you have questions about this policy or how we handle your personal data, please contact us:'
      }
    ],
    contact: {
      company: 'Vega Robotics',
      email: 'contato@vegarobotics.com.br',
      address: 'Av. Alan Turing, 345 - Room 11, Campinas - SP, 13083-898, Brazil'
    }
  };

  // Conteúdo em espanhol
  const contentES = {
    title: 'Política de Privacidad',
    lastUpdated: 'Última actualización: Diciembre de 2024',
    sections: [
      {
        title: '1. Información que Recopilamos',
        content: `Vega Robotics recopila información que usted nos proporciona directamente, como cuando completa formularios de contacto, solicita presupuestos o se suscribe a nuestro boletín. Esta información puede incluir:`,
        list: [
          'Nombre completo',
          'Dirección de correo electrónico corporativo',
          'Teléfono',
          'Nombre de la empresa',
          'Cargo o función',
          'Mensajes enviados a través de formularios'
        ]
      },
      {
        title: '2. Cómo Usamos su Información',
        content: 'Utilizamos la información recopilada para:',
        list: [
          'Responder a sus solicitudes y consultas',
          'Enviar propuestas comerciales cuando se soliciten',
          'Enviar comunicaciones sobre nuestros servicios (con su consentimiento)',
          'Mejorar nuestros productos y servicios',
          'Cumplir con obligaciones legales'
        ]
      },
      {
        title: '3. Compartir Información',
        content: 'No vendemos, alquilamos ni compartimos su información personal con terceros con fines de marketing. Solo podemos compartir información en las siguientes situaciones:',
        list: [
          'Con proveedores de servicios que nos ayudan en nuestras operaciones',
          'Cuando lo exija la ley o una orden judicial',
          'Para proteger nuestros derechos legales'
        ]
      },
      {
        title: '4. Seguridad de los Datos',
        content: 'Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción. Sin embargo, ningún método de transmisión por Internet es 100% seguro.'
      },
      {
        title: '5. Cookies y Tecnologías Similares',
        content: 'Nuestro sitio web puede utilizar cookies y tecnologías similares para mejorar su experiencia de navegación, analizar el tráfico del sitio y personalizar contenido. Puede configurar su navegador para rechazar cookies, pero esto puede afectar la funcionalidad del sitio.'
      },
      {
        title: '6. Sus Derechos',
        content: 'De acuerdo con las leyes de protección de datos aplicables, usted tiene derecho a:',
        list: [
          'Acceder a sus datos personales',
          'Corregir datos incompletos o desactualizados',
          'Solicitar la eliminación de sus datos',
          'Revocar su consentimiento en cualquier momento',
          'Solicitar la portabilidad de los datos'
        ]
      },
      {
        title: '7. Retención de Datos',
        content: 'Mantenemos su información personal durante el tiempo necesario para cumplir con los propósitos descritos en esta política, a menos que la ley exija un período de retención más largo.'
      },
      {
        title: '8. Cambios en esta Política',
        content: 'Podemos actualizar esta política periódicamente. Le recomendamos que revise esta página regularmente para mantenerse informado sobre nuestras prácticas de privacidad.'
      },
      {
        title: '9. Contacto',
        content: 'Si tiene preguntas sobre esta política o sobre cómo tratamos sus datos personales, contáctenos:'
      }
    ],
    contact: {
      company: 'Vega Robotics',
      email: 'contato@vegarobotics.com.br',
      address: 'Av. Alan Turing, 345 - Sala 11, Campinas - SP, 13083-898, Brasil'
    }
  };

  // Seleciona conteúdo baseado no idioma
  const content = language === 'en' ? contentEN : language === 'es' ? contentES : contentPT;

  return (
    <Container>
      <Title>{content.title}</Title>
      <LastUpdated>{content.lastUpdated}</LastUpdated>

      {content.sections.map((section, index) => (
        <Section key={index}>
          <SectionTitle>{section.title}</SectionTitle>
          <Paragraph>{section.content}</Paragraph>
          {section.list && (
            <List>
              {section.list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </List>
          )}
        </Section>
      ))}

      <ContactInfo>
        <Paragraph><strong>{content.contact.company}</strong></Paragraph>
        <Paragraph>
          E-mail: <a href={`mailto:${content.contact.email}`}>{content.contact.email}</a>
        </Paragraph>
        <Paragraph>{content.contact.address}</Paragraph>
      </ContactInfo>
    </Container>
  );
};

export default Privacy;
